// CreateUserStoryDialog.jsx
import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Typography,
    Box,
    Button,
    Autocomplete,
    Snackbar,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
} from "@mui/material";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import AddIcon from "@mui/icons-material/Add";

import { styled } from "@mui/material/styles";

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialog-paper": {
        borderRadius: "12px",
        padding: theme.spacing(0),
        minWidth: "550px",
        background: "linear-gradient(to right bottom, #ffffff, #f8f9ff)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
    },
}));

export default function CreateUserStoryDialog({ open, onClose }) {
    // ---------------------------------------------------------
    // MOCK OPTIONS (Replace with API response later)
    // ---------------------------------------------------------
    const epicOptions = ["Share-Ur-Truck", "Global Logistics Platform", "Mobile App Revamp"];
    const productOwnerOptions = ["Venku Naidu Velle", "Arjun Dev", "Harsha Kumar"];
    const teamLeadOptions = ["Viyyapu Naveen Kumar", "Sumanth", "David"];

    // ---------------------------------------------------------
    // FORM STATE
    // ---------------------------------------------------------
    const [form, setForm] = useState({
        title: "",
        epic: "",
        productOwner: "",
        teamLead: "",
        startDate: null,
        dueDate: null,
        priority: "Medium",
        description: "As a [user type], I want [some goal], so that [reason].",
        value: 1,
        risk: 0,
        storyPoints: 0,
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // ---------------------------------------------------------
    // HANDLERS
    // ---------------------------------------------------------
    const handleFieldChange = (field, value) => {
        setForm({ ...form, [field]: value });
    };

    const handleSave = () => {
        setSnackbar({
            open: true,
            message: "User Story Created Successfully!",
            severity: "success",
        });
        onClose();
    };

    const handleCancel = () => {
        onClose();
    };

    return (
        <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            
            {/* -------------------------------------- */}
            {/* HEADER */}
            {/* -------------------------------------- */}
            <DialogTitle sx={{ p: 0 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                        p: 2.5,
                        borderRadius: "12px 12px 0 0",
                        color: "white",
                    }}
                >
                    <AddIcon sx={{ mr: 1, color: "white", fontSize: 28 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Create New User Story
                    </Typography>
                </Box>
            </DialogTitle>

            {/* -------------------------------------- */}
            {/* CONTENT */}
            {/* -------------------------------------- */}
            <DialogContent sx={{ p: 3 }}>
                <Grid container spacing={2}>

                    {/* User Story Title */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="User Story Title"
                            required
                            fullWidth
                            value={form.title}
                            onChange={(e) => handleFieldChange("title", e.target.value)}
                        />
                    </Grid>

                    {/* Epic Name */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            options={epicOptions}
                            value={form.epic}
                            onChange={(e, v) => handleFieldChange("epic", v)}
                            renderInput={(params) => (
                                <TextField {...params} label="Epic Name" required fullWidth />
                            )}
                        />
                    </Grid>

                    {/* Product Owner */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            options={productOwnerOptions}
                            value={form.productOwner}
                            onChange={(e, v) => handleFieldChange("productOwner", v)}
                            renderInput={(params) => (
                                <TextField {...params} label="Manager" required fullWidth />
                            )}
                        />
                    </Grid>

                    {/* Team Lead */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            options={teamLeadOptions}
                            value={form.teamLead}
                            onChange={(e, v) => handleFieldChange("teamLead", v)}
                            renderInput={(params) => (
                                <TextField {...params} label="Team Lead" fullWidth />
                            )}
                        />
                    </Grid>

                    {/* Start Date */}
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Start Date"
                                value={form.startDate}
                                onChange={(date) => handleFieldChange("startDate", date)}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth required />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>

                    {/* Due Date */}
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Due Date"
                                value={form.dueDate}
                                onChange={(date) => handleFieldChange("dueDate", date)}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth required />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>

                    {/* Priority */}
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Typography sx={{ fontWeight: 600 }}>Priority:</Typography>
                            <Chip
                                label={form.priority}
                                color="warning"
                                sx={{
                                    backgroundColor:
                                        form.priority === "High"
                                            ? "#fee2e2"
                                            : form.priority === "Medium"
                                            ? "#fef3c7"
                                            : "#dcfce7",
                                    color:
                                        form.priority === "High"
                                            ? "#b91c1c"
                                            : form.priority === "Medium"
                                            ? "#b45309"
                                            : "#15803d",
                                    px: 2,
                                    py: 0.5,
                                    fontWeight: 700,
                                }}
                            />
                        </Box>
                    </Grid>

                    {/* Description */}
                    <Grid item xs={12}>
                        <TextField
                            label="Description"
                            multiline
                            rows={3}
                            fullWidth
                            value={form.description}
                            onChange={(e) => handleFieldChange("description", e.target.value)}
                        />
                    </Grid>

                    {/* User Story Details */}
                    {/* <Grid item xs={12}>
                        <Typography
                            sx={{ mb: 1, color: "#2563eb", fontWeight: 600 }}
                        >
                            User Story Details
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Value"
                                    type="number"
                                    fullWidth
                                    value={form.value}
                                    onChange={(e) => handleFieldChange("value", e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="R.I.S.K"
                                    type="number"
                                    fullWidth
                                    value={form.risk}
                                    onChange={(e) => handleFieldChange("risk", e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Story Points"
                                    type="number"
                                    fullWidth
                                    value={form.storyPoints}
                                    onChange={(e) =>
                                        handleFieldChange("storyPoints", e.target.value)
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Grid> */}
                </Grid>
            </DialogContent>

            {/* -------------------------------------- */}
            {/* ACTION BUTTONS */}
            {/* -------------------------------------- */}
            <DialogActions sx={{ p: 3, background: "#f8f9ff" }}>
                <Button
                    onClick={handleCancel}
                    variant="outlined"
                    sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        borderColor: "#94a3b8",
                        color: "#64748b",
                        px: 4,
                    }}
                >
                    Cancel
                </Button>

                <Button
                    onClick={handleSave}
                    variant="contained"
                    sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                        px: 4,
                    }}
                >
                    Save
                </Button>
            </DialogActions>

            {/* -------------------------------------- */}
            {/* SNACKBAR */}
            {/* -------------------------------------- */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </StyledDialog>
    );
}
